import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { getApiErrorMessage } from "@/lib/api-errors";
import { openUpgradeModal } from "@/core/stores/upgradeModalStore";
import { tokenStorage } from "@/core/services/tokenStorage";

const ChatContext = createContext(null);

const STORAGE_KEY_GUEST_SESSION = "homy_guest_chat_session_id";
const STORAGE_KEY_GUEST_MESSAGES = "homy_guest_chat_messages";
const STORAGE_KEY_GUEST_INFO = "homy_guest_info";

export function ChatProvider({ children }) {
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const viewContext = useView?.();
  const currentView = viewContext?.currentView || "operations";

  // Dynamic mode & persona
  const activeMode = isAuthenticated ? "authenticated" : "public";
  const activePersona = useMemo(() => {
    if (!isAuthenticated) return "concierge";
    if (currentView === "contractor") return "contractor";
    if (currentView === "resident") return "resident";
    return "operations";
  }, [isAuthenticated, currentView]);

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [chatPlacement, setChatPlacement] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("homy_chat_placement") || "bottom-right";
    }
    return "bottom-right";
  });

  const updateChatPlacement = useCallback((newPlacement) => {
    setChatPlacement(newPlacement);
    if (typeof window !== "undefined") {
      localStorage.setItem("homy_chat_placement", newPlacement);
    }
  }, []);
  const [unreadCount, setUnreadCount] = useState(0);

  // Guest contact info
  const [guestInfo, setGuestInfoState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_GUEST_INFO);
      return stored ? JSON.parse(stored) : { name: "", email: "", phone: "" };
    } catch {
      return { name: "", email: "", phone: "" };
    }
  });

  const setGuestInfo = useCallback((updates) => {
    setGuestInfoState((prev) => {
      const next = typeof updates === "function" ? updates(prev) : { ...prev, ...updates };
      try {
        sessionStorage.setItem(STORAGE_KEY_GUEST_INFO, JSON.stringify(next));
      } catch {
        /* ignore storage quota */
      }
      return next;
    });
  }, []);

  // Conversation state
  const [messages, setMessages] = useState(() => {
    if (!isAuthenticated) {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY_GUEST_MESSAGES);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const [sessionId, setSessionId] = useState(() => {
    if (!isAuthenticated) {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY_GUEST_SESSION);
        const isGuest = stored && (stored.startsWith("gst_") || stored.startsWith("sess_guest"));
        return isGuest ? stored : null;
      } catch {
        return null;
      }
    }
    return null;
  });

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);

  // Refs for streaming management
  const abortControllerRef = useRef(null);
  const readerRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Handle Auth changes (Clean boundary between guest and authenticated sessions)
  const prevAuthRef = useRef(isAuthenticated);
  useEffect(() => {
    if (prevAuthRef.current !== isAuthenticated) {
      // Abort active stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsStreaming(false);
      setError(null);

      // Clean boundary: Purge guest storage on BOTH login and logout branches
      try {
        sessionStorage.removeItem(STORAGE_KEY_GUEST_SESSION);
        sessionStorage.removeItem(STORAGE_KEY_GUEST_MESSAGES);
        sessionStorage.removeItem(STORAGE_KEY_GUEST_INFO);
      } catch {
        /* ignore storage quota or disabled storage */
      }

      if (isAuthenticated) {
        // Logged in: Clear guest session to prevent cross-contamination
        setMessages([]);
        setSessionId(null);
      } else {
        // Logged out: Clear authenticated messages & reset contact state
        setMessages([]);
        setSessionId(null);
        setGuestInfoState({ name: "", email: "", phone: "" });
      }
      prevAuthRef.current = isAuthenticated;
    }
  }, [isAuthenticated]);

  // Sync session storage on guest messages update (strictly for guest sessions)
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        const isGuestSession =
          !sessionId ||
          (typeof sessionId === "string" &&
            (sessionId.startsWith("gst_") || sessionId.startsWith("sess_guest")));
        if (isGuestSession) {
          sessionStorage.setItem(STORAGE_KEY_GUEST_MESSAGES, JSON.stringify(messages));
          if (sessionId && (sessionId.startsWith("gst_") || sessionId.startsWith("sess_guest"))) {
            sessionStorage.setItem(STORAGE_KEY_GUEST_SESSION, sessionId);
          } else if (!sessionId) {
            sessionStorage.removeItem(STORAGE_KEY_GUEST_SESSION);
          }
        }
      } catch {
        /* ignore quota */
      }
    }
  }, [messages, sessionId, isAuthenticated]);

  // Reset unread count when widget opens
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Cooperative cancellation
  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (readerRef.current) {
      try {
        readerRef.current.cancel();
      } catch {
        /* ignore */
      }
      readerRef.current = null;
    }
    setIsStreaming(false);
    setMessages((prev) =>
      prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m))
    );
  }, []);

  // Clear conversation
  const clearHistory = useCallback(() => {
    abortStream();
    setMessages([]);
    setUsage(null);
    setError(null);
    setSessionId(null);
    if (!isAuthenticated) {
      sessionStorage.removeItem(STORAGE_KEY_GUEST_SESSION);
      sessionStorage.removeItem(STORAGE_KEY_GUEST_MESSAGES);
    }
  }, [abortStream, isAuthenticated]);

  // Dispatch message and parse SSE stream
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      setError(null);
      abortStream();

      const now = new Date();
      const timestampStr = now.toISOString();
      const userMessageId = `usr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const assistantMessageId = `asst_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

      const userMessage = {
        id: userMessageId,
        role: "user",
        content: trimmed,
        timestamp: timestampStr,
      };

      const assistantPlaceholder = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        timestamp: timestampStr,
        isStreaming: true,
        cards: [],
        suggestionChips: [],
      };

      setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const token = tokenStorage.getAccessToken();
      const apiBaseUrl =
        import.meta.env.DEV && import.meta.env.VITE_USE_DIRECT_API !== "true"
          ? ""
          : import.meta.env.VITE_API_URL || "";
      const endpoint = `${apiBaseUrl}/api/agents/chat`;

      const activeLocale = i18n?.language ? i18n.language.split("-")[0] : "en";

      const headers = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "Accept-Language": activeLocale,
      };
      if (isAuthenticated && token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const requestPayload = {
        message: trimmed,
        sessionId: sessionIdRef.current || null,
        model: "deepseek-chat",
        locale: activeLocale,
        guestInfo:
          !isAuthenticated && (guestInfo.name || guestInfo.email || guestInfo.phone)
            ? guestInfo
            : undefined,
        messageHistory: messages.map(m => ({ role: m.role, content: m.content })),
      };

      let accumulatedContent = "";

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(requestPayload),
          signal: controller.signal,
        });

        if (!response.ok) {
          let errorData = null;
          try {
            errorData = await response.json();
          } catch {
            try {
              errorData = await response.text();
            } catch {
              /* empty */
            }
          }

          if (
            response.status === 402 ||
            (errorData && typeof errorData === "object" && errorData.code === "QUOTA_EXCEEDED")
          ) {
            openUpgradeModal(errorData);
          }

          const fallback = `HTTP ${response.status}: ${response.statusText}`;
          throw new Error(getApiErrorMessage({ response: { data: errorData } }, fallback));
        }

        if (!response.body) {
          throw new Error("ReadableStream not supported by browser.");
        }

        const reader = response.body.getReader();
        readerRef.current = reader;
        const decoder = new TextDecoder();
        let buffer = "";
        let streamUsage = null;
        let emittedCards = [];
        let emittedChips = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          buffer = buffer.replace(/\r\n/g, "\n");

          let eventIndex;
          while ((eventIndex = buffer.indexOf("\n\n")) !== -1) {
            const rawBlock = buffer.slice(0, eventIndex);
            buffer = buffer.slice(eventIndex + 2);

            if (!rawBlock.trim()) continue;

            let eventType = "message";
            let dataPayload = "";

            for (const line of rawBlock.split("\n")) {
              const lineTrimmed = line.trim();
              if (!lineTrimmed || lineTrimmed.startsWith(":")) continue;

              if (lineTrimmed.startsWith("event:")) {
                eventType = lineTrimmed.slice(6).trim();
              } else if (lineTrimmed.startsWith("data:")) {
                dataPayload = lineTrimmed.slice(5).trim();
              }
            }

            if (!dataPayload) continue;

            let parsed;
            try {
              parsed = JSON.parse(dataPayload);
            } catch (pErr) {
              console.warn("[ChatContext] Stream chunk parse error:", dataPayload, pErr);
              continue;
            }

            if (parsed.sessionId && parsed.sessionId !== sessionIdRef.current) {
              setSessionId(parsed.sessionId);
            }

            // Options / Suggestion Chips event
            if (eventType === "options" || parsed.suggestionChips || parsed.options) {
              const chips = parsed.suggestionChips || parsed.options || [];
              emittedChips = Array.isArray(chips) ? chips : [];
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, suggestionChips: emittedChips, options: emittedChips }
                    : msg
                )
              );
            }

            // Card event
            if (eventType === "card" || parsed.card || parsed.card_type) {
              const cardData = parsed.card || (parsed.card_type ? parsed : parsed);
              emittedCards.push(cardData);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, cards: [...emittedCards] }
                    : msg
                )
              );
            }

            // Text delta chunk
            if (parsed.chunk || parsed.text) {
              accumulatedContent += parsed.chunk || parsed.text;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: accumulatedContent }
                    : msg
                )
              );
            }

            // Escalation metadata
            if (parsed.escalation) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, escalation: parsed.escalation }
                    : msg
                )
              );
            }

            // Token usage
            if (parsed.usage) {
              streamUsage = parsed.usage;
              setUsage(parsed.usage);
            }

            // Stream complete
            if (parsed.done) {
              if (parsed.cards && Array.isArray(parsed.cards)) {
                emittedCards = parsed.cards;
              }
              if (parsed.suggestionChips || parsed.options) {
                const chips = parsed.suggestionChips || parsed.options;
                emittedChips = Array.isArray(chips) ? chips : [];
              }
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? {
                        ...msg,
                        content: accumulatedContent,
                        cards: emittedCards,
                        suggestionChips: emittedChips,
                        options: emittedChips,
                        usage: streamUsage || msg.usage,
                        isStreaming: false,
                      }
                    : msg
                )
              );
            }

            if (parsed.error) {
              throw new Error(parsed.error);
            }
          }
        }

        // Finalize assistant message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: accumulatedContent,
                  cards: emittedCards,
                  suggestionChips: emittedChips,
                  options: emittedChips,
                  usage: streamUsage || msg.usage,
                  isStreaming: false,
                }
              : msg
          )
        );

        if (!isOpen) {
          setUnreadCount((c) => c + 1);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        const errMsg = err.message || "Failed to communicate with assistant.";
        setError(errMsg);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: accumulatedContent || "Sorry, an error occurred while processing your request.",
                  isStreaming: false,
                  isError: true,
                  errorMessage: errMsg,
                }
              : msg
          )
        );
      } finally {
        setIsStreaming(false);
        abortControllerRef.current = null;
        readerRef.current = null;
      }
    },
    [isStreaming, abortStream, isAuthenticated, guestInfo, isOpen, messages, i18n?.language]
  );

  // Global event listeners: ⌘J, open-support-chat, homy:open-copilot
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setIsOpen((open) => !open);
      }
    };

    const handleOpenSupportChat = (event) => {
      setIsOpen(true);
      if (event?.detail) {
        if (event.detail.name || event.detail.email || event.detail.phone) {
          setGuestInfo({
            name: event.detail.name || "",
            email: event.detail.email || "",
            phone: event.detail.phone || "",
          });
        }
        if (event.detail.initialPrompt) {
          sendMessage(event.detail.initialPrompt);
        }
      }
    };

    const handleOpenCopilot = () => {
      setIsOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("open-support-chat", handleOpenSupportChat);
    window.addEventListener("homy:open-copilot", handleOpenCopilot);
    window.__openSupportChat = (detail) => handleOpenSupportChat({ detail });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("open-support-chat", handleOpenSupportChat);
      window.removeEventListener("homy:open-copilot", handleOpenCopilot);
      delete window.__openSupportChat;
    };
  }, [setGuestInfo, sendMessage]);

  const value = {
    isOpen,
    setIsOpen,
    chatPlacement,
    updateChatPlacement,
    toggleChat: () => setIsOpen((prev) => !prev),
    openChat: (options) => {
      setIsOpen(true);
      if (options?.prompt) {
        sendMessage(options.prompt);
      }
    },
    closeChat: () => setIsOpen(false),
    activeMode,
    activePersona,
    messages,
    sendMessage,
    isStreaming,
    unreadCount,
    clearHistory,
    guestInfo,
    setGuestInfo,
    sessionId,
    setSessionId,
    error,
    usage,
    abortStream,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export default ChatContext;
